/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Chip, Dialog, InlineNotification, Link, TextField, Typography2 } from '@coursera/cds-core';
import { EyeIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import type { AIMessageDraft } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import MessageBodyEditor from 'bundles/enterprise-admin-messaging-v2/components/common/MessageBodyEditor';
import PersonalizationTagActions from 'bundles/enterprise-admin-messaging-v2/components/common/PersonalizationTagActions';
import { useMessagingTrackingData } from 'bundles/enterprise-admin-messaging-v2/hooks/useMessagingTrackingData';
import { useUploadManualCsv } from 'bundles/enterprise-admin-messaging-v2/hooks/useUploadManualCsv';
import {
  MESSAGING_BUTTON,
  MESSAGING_MODAL,
  MESSAGING_TEXT_INPUT,
} from 'bundles/enterprise-admin-messaging-v2/utils/eventing';
import { truncateFileName } from 'bundles/enterprise-admin-messaging-v2/utils/truncateFileName';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

const CSV_TEMPLATE_URL =
  'https://coursera-university-assets.s3.us-east-1.amazonaws.com/program-assets/csv/messaging-learner-list-template.csv';

type Props = {
  draft: AIMessageDraft;
  updateDraft: (partial: Partial<AIMessageDraft>) => void;
  onSend: () => void;
  /** Switches to the AI Define-goal screen ("Draft using AI"). */
  onDraftWithAI: () => void;
  onPreviewEmail: () => void;
  /** Org the uploaded CSV is validated + sent under. */
  organizationId: string;
  /** True while the message is being sent — disables Send to prevent double-sends. */
  sending?: boolean;
  /** Error surfaced when the send fails, so the admin gets feedback and can retry. */
  sendError?: string | null;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  field: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  fieldLabel: css`
    color: var(--cds-color-neutral-primary);
  `,
  audienceHelper: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  /* Filled audience: learner count followed by the removable file chip. */
  audienceFilled: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
    flex-wrap: wrap;
  `,
  ctaRow: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--cds-spacing-200);
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
    width: 100%;
  `,
  previewLink: css`
    margin-left: auto;
  `,
};

/**
 * Manual-draft screen of the message modal. The admin uploads an audience CSV,
 * writes the subject / message / CTA by hand, and then sends — or hands off to
 * the AI flow, or previews the email. Reachable only from the AI modal's
 * "Draft a message manually" link, so it shares the modal's draft state.
 */
const ManualDraftScreen = ({
  draft,
  updateDraft,
  onSend,
  onDraftWithAI,
  onPreviewEmail,
  organizationId,
  sending = false,
  sendError = null,
}: Props) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const track = useTracker();
  const trackingData = useMessagingTrackingData();
  const { upload, isUploading } = useUploadManualCsv();
  const [uploadError, setUploadError] = React.useState<string | null>(null);

  // Impression: fires once when the manual-compose screen appears.
  React.useEffect(() => {
    track('view_modal', { ...trackingData, modal: { name: MESSAGING_MODAL.MANUAL_COMPOSE } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Track a text-input focus for the manual-compose form.
   * @param name - The text-input taxonomy name of the focused field.
   */
  const trackTextInputFocus = React.useCallback<
    (name: (typeof MESSAGING_TEXT_INPUT)[keyof typeof MESSAGING_TEXT_INPUT]) => void
  >(
    (name) => {
      track('click_text_input', {
        textInput: { name },
        ...trackingData,
        modal: { name: MESSAGING_MODAL.MANUAL_COMPOSE },
      });
    },
    [track, trackingData]
  );

  const handleUploadClick = React.useCallback<() => void>(() => {
    track('click_button', {
      button: { name: MESSAGING_BUTTON.UPLOAD_CSV },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.MANUAL_COMPOSE },
    });
    fileInputRef.current?.click();
  }, [track, trackingData]);

  /** Track the "Draft using AI" click, then hand off to the AI flow. */
  const handleDraftWithAI = React.useCallback<() => void>(() => {
    track('click_button', {
      button: { name: MESSAGING_BUTTON.USE_AI_PROMPT },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.MANUAL_COMPOSE },
    });
    onDraftWithAI();
  }, [track, trackingData, onDraftWithAI]);

  /** Track the "Preview email" click, then run the existing handler. */
  const handlePreviewEmail = React.useCallback<() => void>(() => {
    track('click_button', {
      button: { name: MESSAGING_BUTTON.PREVIEW_MESSAGE },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.MANUAL_COMPOSE },
    });
    onPreviewEmail();
  }, [track, trackingData, onPreviewEmail]);

  const handleFileChange = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset so selecting the same file again re-triggers onChange.
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (!file) return;
      setUploadError(null);

      const result = await upload(file, organizationId);
      if (result.status === 'success') {
        // `validLearnerCount` is set for a sync preview; an async preview returns
        // no count, so audienceCount stays 0 and the sent screen confirms without
        // a number.
        updateDraft({
          audienceFileName: file.name,
          manualCsvS3Object: result.s3Object,
          audienceCount: result.validLearnerCount ?? 0,
        });
      } else {
        setUploadError(result.error);
      }
    },
    [upload, organizationId, updateDraft]
  );

  const handleRemoveCsv = React.useCallback<() => void>(() => {
    setUploadError(null);
    updateDraft({ audienceFileName: undefined, manualCsvS3Object: undefined, audienceCount: 0 });
  }, [updateDraft]);

  const hasAudience = Boolean(draft.manualCsvS3Object);

  // Send stays disabled until the audience CSV is uploaded and the subject +
  // message are filled in. The CTA (label + URL) is optional — a message can be
  // sent without a call-to-action button.
  const canSend = hasAudience && !isUploading && draft.subject.trim().length > 0 && draft.message.trim().length > 0;

  return (
    <React.Fragment>
      <Dialog.Content>
        <div css={styles.body}>
          {/* Persistent live region so the screen reader announces upload/send
              errors when they appear (per the live-region a11y rule). */}
          <div role="alert">
            {uploadError && (
              <InlineNotification severity="error" data-testid="manual-draft-upload-error">
                {_t('We couldn’t process that CSV. Check the file and try again.')}
              </InlineNotification>
            )}
            {sendError && (
              <InlineNotification severity="error" data-testid="manual-draft-send-error">
                {sendError}
              </InlineNotification>
            )}
          </div>
          <InlineNotification
            severity="information"
            inlineAction={
              <Button
                size="small"
                variant="ghost"
                onClick={handleUploadClick}
                loading={isUploading}
                disabled={isUploading}
                data-testid="manual-draft-upload-csv"
              >
                {isUploading ? _t('Uploading…') : _t('Upload CSV')}
              </Button>
            }
          >
            <FormattedMessage
              message={_t('Download and complete the {templateLink}. Include Name and Email of learners.')}
              templateLink={
                <Link href={CSV_TEMPLATE_URL} download data-testid="manual-draft-csv-template">
                  {_t('csv template')}
                </Link>
              }
            />
          </InlineNotification>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            hidden
            onChange={handleFileChange}
            data-testid="manual-draft-csv-input"
          />

          <div css={styles.field}>
            <Typography2 component="span" variant="subtitleMedium" css={styles.fieldLabel}>
              {_t('Audience')}
            </Typography2>
            {hasAudience ? (
              <div css={styles.audienceFilled} data-testid="manual-draft-audience-filled">
                <Typography2 component="span" variant="bodyPrimary">
                  {draft.audienceCount > 0 ? (
                    <FormattedMessage
                      message={_t('{count, plural, one {# learner} other {# learners}}')}
                      count={draft.audienceCount}
                    />
                  ) : (
                    // Async preview: the upload is accepted but the count isn't known yet.
                    _t('Learners from uploaded CSV')
                  )}
                </Typography2>
                <Chip variant="delete" onClick={handleRemoveCsv} data-testid="manual-draft-audience-chip">
                  {truncateFileName(draft.audienceFileName ?? '')}
                </Chip>
              </div>
            ) : (
              <Typography2 component="span" variant="bodyPrimary" css={styles.audienceHelper}>
                {_t('Upload CSV with learners you want to message')}
              </Typography2>
            )}
          </div>

          <TextField
            label={_t('Subject')}
            value={draft.subject}
            onChange={(event) => updateDraft({ subject: event.target.value })}
            onFocus={() => trackTextInputFocus(MESSAGING_TEXT_INPUT.SUBJECT)}
            placeholder={_t('Add your email subject line')}
            data-testid="manual-draft-subject"
          />

          <div css={styles.field}>
            <Typography2 component="span" variant="subtitleMedium" css={styles.fieldLabel}>
              {_t('Message')}
            </Typography2>
            <MessageBodyEditor
              value={draft.message}
              onChange={(message) => updateDraft({ message })}
              renderToolbarActions={(insertText) => <PersonalizationTagActions onInsert={insertText} />}
              aria-label={_t('Message body')}
              data-testid="manual-draft-message-body"
              onFocus={() => trackTextInputFocus(MESSAGING_TEXT_INPUT.BODY)}
            />
          </div>

          <div css={styles.ctaRow}>
            <TextField
              label={_t('Call to action')}
              value={draft.ctaText}
              onChange={(event) => updateDraft({ ctaText: event.target.value })}
              onFocus={() => trackTextInputFocus(MESSAGING_TEXT_INPUT.CTA_LABEL)}
              placeholder={_t('Add label for CTA')}
              data-testid="manual-draft-cta-text"
            />
            <TextField
              label={_t('Button URL')}
              value={draft.ctaUrl}
              onChange={(event) => updateDraft({ ctaUrl: event.target.value, isCtaUrlEdited: true })}
              onFocus={() => trackTextInputFocus(MESSAGING_TEXT_INPUT.BUTTON_URL)}
              placeholder={_t('Add url')}
              type="url"
              autoComplete="url"
              inputProps={{ inputMode: 'url' }}
              data-testid="manual-draft-cta-url"
            />
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div css={styles.actions}>
          <Button
            variant="primary"
            onClick={onSend}
            disabled={!canSend}
            loading={sending}
            data-testid="manual-draft-send"
          >
            {_t('Send')}
          </Button>
          <Button variant="ghost" onClick={handleDraftWithAI} data-testid="manual-draft-draft-with-ai">
            {_t('Draft using AI')}
          </Button>
          <Button
            variant="ghost"
            edgeAlign="end"
            onClick={handlePreviewEmail}
            icon={<EyeIcon />}
            iconPosition="before"
            css={styles.previewLink}
            data-testid="manual-draft-preview-email"
          >
            {_t('Preview')}
          </Button>
        </div>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default ManualDraftScreen;
